// hooks/useTurnstile.ts
import { useEffect, useRef, useCallback, useState } from "react";
import { loadScript } from "@/utils/scriptLoader";
import { TURNSTILE_SITE_KEY } from "@/recourses/constants";

interface TurnstileOptions {
  sitekey?: string;
  action?: string;
  theme?: "auto" | "light" | "dark";
  size?: "normal" | "flexible" | "compact";
  enabled?: boolean;
  onSuccess?: (token: string) => void;
  onError?: () => void;
}

interface TurnstileInstance {
  render: (container: string | HTMLElement, options: any) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
  getResponse: (widgetId: string) => string;
  isExpired: (widgetId: string) => boolean;
}

declare global {
  interface Window {
    turnstile?: TurnstileInstance;
  }
}

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export const useTurnstile = (options: TurnstileOptions) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const optionsRef = useRef(options);

  // update options without re-renders
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // load script only if enabled and not already loaded
  useEffect(() => {
    if (!options.enabled) {
      return;
    }

    let mounted = true;

    const initTurnstile = async () => {
      try {
        await loadScript(TURNSTILE_SCRIPT_URL, {
          id: "turnstile-script",
          defer: true,
        });

        if (mounted && window.turnstile) {
          setIsReady(true);
        }
      } catch (err) {
        setError("Error al cargar la verificación de seguridad");
        optionsRef.current.onError?.();
      }
    };

    initTurnstile();

    return () => {
      mounted = false;
    };
  }, [options.enabled]);

  useEffect(() => {
    if (!isReady || !containerRef.current || !window.turnstile || !options.enabled) {
      return;
    }

    if (widgetIdRef.current) {
      return;
    }

    const { sitekey = TURNSTILE_SITE_KEY, action, theme, size, onSuccess, onError } = optionsRef.current;
    // TODO: remove this log
    console.log("[Turnstile] sitekey present:", !!sitekey);

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey,
        action,
        theme: theme || "light",
        size: size || "normal",
        callback: (token: string) => {
          setError(null);
          onSuccess?.(token);
        },
        "error-callback": () => {
          setError("Error en la verificación de seguridad");
          onError?.();
        },
        "expired-callback": () => {
          setError("El token de verificación ha expirado");
          onError?.();
        },
        "timeout-callback": () => {
          setError("Tiempo de verificación agotado");
          onError?.();
        },
      });
    } catch (err) {
      setError("Error al cargar la verificación de seguridad");
      onError?.();
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
        setError(null);
      }
    };
  }, [isReady, options.enabled]);

  const reset = useCallback(() => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      setError(null);
    }
  }, []);

  return {
    containerRef,
    isReady,
    error,
    reset,
  };
};
